import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import PhotoCarousel from "@/components/PhotoCarousel";
import { PictogramImage } from "@/components/PictogramImage";
import { PUBLIC_PALETTE } from "@/lib/publicPalette";
import { groupPictograms } from "@/lib/pictogramGroups";
import { Loader2, Globe, ChevronDown, ChevronLeft, Star, Heart, Circle, Square, Diamond, Triangle, Hexagon } from "lucide-react";

const RATING_ICONS = {
  star: Star, heart: Heart,
  circle: Circle, square: Square, diamond: Diamond, triangle: Triangle, hexagon: Hexagon,
};

const LANGS = [
  { code: "it", label: "IT" }, { code: "en", label: "EN" },
  { code: "fr", label: "FR" }, { code: "es", label: "ES" }, { code: "de", label: "DE" },
];

const L = {
  it: { back: "Catalogo", code: "Codice", quantity: "Quantità", price: "Prezzo",
        origin: "Paese di origine", description: "Descrizione",
        characteristics: "Caratteristiche", usage: "Modo d'uso", warnings: "Avvertenze",
        pictograms: "Smaltimento", inci: "Ingredienti (INCI)", barcode: "Codice a barre",
        safety: "Scheda sicurezza", view: "Visualizza",
        buy_now: "Acquista ora", translating: "Traduzione in corso..." },
  en: { back: "Catalogue", code: "Code", quantity: "Quantity", price: "Price",
        origin: "Country of origin", description: "Description",
        characteristics: "Characteristics", usage: "How to use", warnings: "Warnings",
        pictograms: "Disposal", inci: "Ingredients (INCI)", barcode: "Barcode",
        safety: "Safety sheet", view: "View",
        buy_now: "Buy now", translating: "Translating..." },
  fr: { back: "Catalogue", code: "Code", quantity: "Quantité", price: "Prix",
        origin: "Pays d'origine", description: "Description",
        characteristics: "Caractéristiques", usage: "Mode d'emploi", warnings: "Avertissements",
        pictograms: "Élimination", inci: "Ingrédients (INCI)", barcode: "Code-barres",
        safety: "Fiche de sécurité", view: "Consulter",
        buy_now: "Acheter", translating: "Traduction..." },
  es: { back: "Catálogo", code: "Código", quantity: "Cantidad", price: "Precio",
        origin: "País de origen", description: "Descripción",
        characteristics: "Características", usage: "Modo de uso", warnings: "Advertencias",
        pictograms: "Eliminación", inci: "Ingredientes (INCI)", barcode: "Código de barras",
        safety: "Ficha de seguridad", view: "Ver",
        buy_now: "Comprar", translating: "Traduciendo..." },
  de: { back: "Katalog", code: "Code", quantity: "Menge", price: "Preis",
        origin: "Herkunftsland", description: "Beschreibung",
        characteristics: "Eigenschaften", usage: "Anwendung", warnings: "Warnhinweise",
        pictograms: "Entsorgung", inci: "Inhaltsstoffe (INCI)", barcode: "Strichcode",
        safety: "Sicherheitsdatenblatt", view: "Ansehen",
        buy_now: "Kaufen", translating: "Übersetzung läuft..." },
};

const CURRENCY_SYMBOL = { EUR: "€", USD: "$", GBP: "£" };

export default function BrandProductDetail() {
  const { slug: brandSlug, productSlug } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [lang, setLang] = useState("it");
  const [translating, setTranslating] = useState(false);
  const [translations, setTranslations] = useState({});
  const [udmConv, setUdmConv] = useState(null);
  const [showLangs, setShowLangs] = useState(false);

  useEffect(() => {
    document.title = "Cosmely";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    api.get(`/public/products/${productSlug}`)
      .then(r => {
        if (r.data?.disabled) { nav("/disabled"); return; }
        setData(r.data);
      })
      .catch(() => nav("/disabled"));
    api.get(`/public/vetrina-detail-layout`, { params: { product_slug: productSlug } })
      .then(r => setBlocks(r.data))
      .catch(() => setBlocks([]));
  }, [productSlug, nav]);

  const t = useMemo(() => L[lang] || L.it, [lang]);

  const switchLang = async (newLang) => {
    setShowLangs(false);
    if (newLang === lang) return;
    setLang(newLang);
    const cacheKey = `cosm_tr_${productSlug}_${newLang}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      const obj = JSON.parse(cached);
      setTranslations(obj.translations);
      setUdmConv(obj.udm_conversion);
      return;
    }
    if (newLang === "it") { setTranslations({}); setUdmConv(null); return; }
    setTranslating(true);
    try {
      const r = await api.post(`/public/products/${productSlug}/translate`, { target_lang: newLang });
      setTranslations(r.data.translations || {});
      setUdmConv(r.data.udm_conversion);
      sessionStorage.setItem(cacheKey, JSON.stringify(r.data));
    } catch { /* silent */ }
    finally { setTranslating(false); }
  };

  if (!data) {
    return (
      <div className="public-page flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#8A8579]"/>
      </div>
    );
  }

  const tx = (key, original) => (lang === "it" ? original : (translations[key] || original));
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const ratingIconKey = data.brand?.rating_icon || "star";
  const ratingIconUrl = data.brand?.rating_icon_url || null;
  const youtubeId = data.youtube_url ? extractYoutubeId(data.youtube_url) : null;
  const enabledBlocks = blocks || [];

  const priceStr = data.price_value != null
    ? `${CURRENCY_SYMBOL[data.price_currency] || data.price_currency || "€"} ${Number(data.price_value).toFixed(2).replace(".", ",")}`
    : null;

  const renderBlock = (b) => {
    const k = b.block_key;
    switch (k) {
      case "photos": {
        if (!data.photos?.length) return null;
        return (
          <div key={k} className="pp-block pp-block--media" data-testid="detail-block-photos">
            <PhotoCarousel photos={data.photos} apiBase={apiBase} alt={data.name} />
          </div>
        );
      }
      case "brand": {
        if (!data.brand?.name && !data.brand?.logo_url) return null;
        return (
          <div key={k} className="pp-block pp-brand-mark" data-testid="detail-block-brand">
            {data.brand.logo_url ? (
              <img src={`${apiBase}${data.brand.logo_url}`} alt={data.brand.name || ""}/>
            ) : (
              <span className="pp-eyebrow">{data.brand.name}</span>
            )}
          </div>
        );
      }
      case "name":
        return (
          <div key={k} className="pp-block" data-testid="detail-block-name">
            <h1 className="pp-title">{data.name}</h1>
            {(data.subcategory || data.category) && (
              <div className="pp-subtitle">{data.subcategory || data.category}</div>
            )}
          </div>
        );
      case "code":
        return data.code ? (
          <div key={k} className="pp-block" data-testid="detail-block-code">
            <div className="pp-meta-row"><span>{t.code} <strong>{data.code}</strong></span></div>
          </div>
        ) : null;
      case "quantity":
        return data.quantity_value ? (
          <div key={k} className="pp-block" data-testid="detail-block-quantity">
            <div className="pp-meta-row">
              <span>{t.quantity} <strong>{data.quantity_value} {data.udm}{udmConv ? ` / ${udmConv}` : ""}</strong></span>
            </div>
          </div>
        ) : null;
      case "price":
        return priceStr ? (
          <div key={k} className="pp-block" data-testid="detail-block-price">
            <div className="pp-meta-row"><span>{t.price} <strong>{priceStr}</strong></span></div>
          </div>
        ) : null;
      case "origin": {
        const originName = tx("origin_country", data.origin_country_name);
        return originName ? (
          <div key={k} className="pp-block" data-testid="detail-block-origin">
            <div className="pp-meta-row"><span>{t.origin} <strong>{originName}</strong></span></div>
          </div>
        ) : null;
      }
      case "description": {
        const txt = tx("description", data.description);
        return txt ? (
          <div key={k} className="pp-block" data-testid="detail-block-description">
            <h3 className="pp-block__heading">{t.description}</h3>
            <div className="pp-block__body prose-rt" dangerouslySetInnerHTML={{ __html: txt }}/>
          </div>
        ) : null;
      }
      case "characteristics": {
        const filled = (data.characteristics || []).filter(c => Number(c.value) > 0);
        return filled.length ? (
          <div key={k} className="pp-block" data-testid="detail-block-characteristics">
            <h3 className="pp-block__heading">{t.characteristics}</h3>
            <div className="pp-block__body">
              {filled.map(c => (
                <div key={c.name} className="pp-char-row">
                  <span>{tx(`char_${c.name}`, c.name)}</span>
                  <Rating value={c.value} brandColor="#000000"
                          iconKey={ratingIconKey} iconUrl={ratingIconUrl}/>
                </div>
              ))}
            </div>
          </div>
        ) : null;
      }
      case "usage": {
        const txt = tx("usage", data.usage);
        return txt ? (
          <div key={k} className="pp-block" data-testid="detail-block-usage">
            <h3 className="pp-block__heading">{t.usage}</h3>
            <div className="pp-block__body prose-rt" dangerouslySetInnerHTML={{__html: txt}}/>
          </div>
        ) : null;
      }
      case "warnings": {
        const txt = tx("warnings", data.warnings);
        return txt ? (
          <div key={k} className="pp-block" data-testid="detail-block-warnings">
            <h3 className="pp-block__heading">{t.warnings}</h3>
            <div className="pp-warning-body prose-rt" dangerouslySetInnerHTML={{__html: txt}}/>
          </div>
        ) : null;
      }
      case "pictograms":
        return data.pictograms?.length ? (() => {
          const groups = groupPictograms(data.pictogram_categories || [], data.pictograms, t.pictograms)
            .map(g => ({ ...g, name: g.id ? tx(`pcat_${g.id}`, g.name) : g.name }));
          return groups.map(g => (
            <div key={`${k}-${g.id || "none"}`}
                 className="pp-block"
                 data-testid={g.id ? `detail-block-pictogram-group-${g.id}` : "detail-block-pictograms"}>
              <h3 className="pp-block__heading">{g.name}</h3>
              <div className="grid grid-cols-3 gap-4">
                {g.items.map(p => (
                  <div key={p.id} className="pp-picto">
                    <div className="pp-picto__icon-box">
                      {p.icon_url && <PictogramImage pictogram={p} className="w-full h-full"
                        alt={tx(`picto_${p.id}`, p.name)} testId={`catalog-picto-image-${p.id}`} />}
                    </div>
                    <span className="pp-picto__label" data-testid={`catalog-picto-label-${p.id}`}>{tx(`picto_${p.id}`, p.name)}</span>
                  </div>
                ))}
              </div>
            </div>
          ));
        })() : null;
      case "inci":
        return data.ingredients?.length ? (
          <div key={k} className="pp-block" data-testid="detail-block-inci">
            <h3 className="pp-block__heading">{t.inci}</h3>
            <div className="pp-inci">{data.ingredients.join(", ")}.</div>
          </div>
        ) : null;
      case "barcode":
        return data.barcode_number ? (
          <div key={k} className="pp-block" data-testid="detail-block-barcode">
            <div className="pp-meta-row">
              <span>{t.barcode} <strong className="font-mono">{data.barcode_number}</strong></span>
            </div>
          </div>
        ) : null;
      case "video":
        return youtubeId ? (
          <div key={k} className="pp-block pp-block--media" data-testid="detail-block-video">
            <div className="aspect-video bg-black">
              <iframe src={`https://www.youtube.com/embed/${youtubeId}`}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen title="Video"/>
            </div>
          </div>
        ) : null;
      case "safety_sheet":
        return data.safety_sheet_url ? (
          <div key={k} className="pp-block" data-testid="detail-block-safety">
            <button onClick={() => window.location.href = `/p/${productSlug}/safety`}
                    className="pp-btn"
                    data-testid="detail-safety-view-btn">
              {t.safety} — {t.view}
            </button>
          </div>
        ) : null;
      case "buy_now":
        return data.website_url ? (
          <div key={k} className="pp-block" data-testid="detail-block-buy-now">
            <a href={data.website_url}
               target="_blank"
               rel="noopener noreferrer nofollow"
               className="pp-btn pp-btn--filled"
               data-testid="detail-buy-now-btn">
              {t.buy_now}
            </a>
          </div>
        ) : null;
      default: return null;
    }
  };

  return (
    <div className="public-page"
          data-testid="brand-product-detail"
          style={PUBLIC_PALETTE}>
      <header className="sticky top-0 z-30 backdrop-blur"
              style={{ borderBottom: `1px solid var(--brand)`, background: "var(--surface)" }}>
        <div className="flex justify-between items-center px-5 py-3">
          <button onClick={() => nav(`/b/${brandSlug}`)}
                  className="text-[11px] tracking-[0.25em] uppercase flex items-center gap-1.5"
                  style={{ color: "var(--brand)" }}
                  data-testid="detail-back-btn">
            <ChevronLeft className="w-3.5 h-3.5" strokeWidth={1.5}/>
            {t.back}
          </button>
          <div className="relative">
            <button onClick={()=>setShowLangs(s=>!s)}
                    className="text-[11px] tracking-[0.25em] uppercase flex items-center gap-1.5"
                    style={{ color: "var(--brand)" }}
                    data-testid="detail-lang-selector">
              <Globe className="w-3.5 h-3.5" strokeWidth={1.5}/>
              {lang.toUpperCase()}
              <ChevronDown className="w-3 h-3" strokeWidth={1.5}/>
            </button>
            {showLangs && (
              <div className="absolute right-0 top-full mt-2 z-50 min-w-[80px]"
                    style={{ border: `1px solid var(--brand)`, background: "var(--surface)" }}>
                {LANGS.map(l => (
                  <button key={l.code} onClick={()=>switchLang(l.code)}
                          className={`block px-4 py-2.5 text-[11px] tracking-[0.25em] uppercase w-full text-left transition-colors pp-lang-opt ${l.code === lang ? "font-semibold" : ""}`}
                          data-testid={`detail-lang-option-${l.code}`}>
                    {l.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        {translating && (
          <div className="text-[10px] tracking-[0.3em] uppercase py-2 text-center flex items-center justify-center gap-2"
               style={{ background: "var(--brand)", color: "var(--surface)" }}>
            <Loader2 className="w-3 h-3 animate-spin"/> {t.translating}
          </div>
        )}
      </header>

      <article className="pb-6">
        <div className="pp-frame">
          {enabledBlocks.map(renderBlock).filter(Boolean)}
        </div>
      </article>
    </div>
  );
}

function Rating({ value, brandColor, iconKey, iconUrl }) {
  const steps = [1, 2, 3, 4, 5];
  if (iconUrl) {
    return (
      <div className="flex gap-[3px] items-center">
        {steps.map(v => (
          <img key={v} src={iconUrl} alt=""
                className="w-4 h-4 object-contain"
                style={{ opacity: v <= value ? 1 : 0.18 }}/>
        ))}
      </div>
    );
  }
  const Icon = RATING_ICONS[iconKey] || Star;
  return (
    <div className="flex gap-[3px] items-center">
      {steps.map(v => {
        const active = v <= value;
        return (
          <Icon key={v}
                className="w-4 h-4"
                strokeWidth={1.5}
                style={{
                  color: active ? brandColor : "#737373",
                  fill: active ? brandColor : "transparent",
                }}/>
        );
      })}
    </div>
  );
}

function extractYoutubeId(url) {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return u.pathname.slice(1);
    return u.searchParams.get("v");
  } catch { return null; }
}
